/** Passkeys (Face ID / Touch ID / Windows Hello / phone) on top of the normal password sign-in. */
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import type { PasskeyDTO } from '@tc/contracts';
import { api } from './api.ts';
import { desktop } from './desktop.ts';
import { isNative } from './native.ts';

export const passkeysSupported = (): boolean => {
  // The desktop app (Electron) has no system passkey prompt; the phone apps have their own Face ID sign-in
  // (faceid.ts), which needs no extra Apple sheet. Passkeys stay for browsers.
  if (desktop || isNative) return false;
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
};

/** The person closed the system prompt (or it timed out): not an error worth showing. */
export const cancelled = (err: unknown): boolean => err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError');

/** Never wait forever on a system prompt that did not appear. */
function withTimeout<T>(p: Promise<T>, ms = 90_000): Promise<T> {
  return Promise.race([p, new Promise<never>((_, reject) => setTimeout(() => reject(Object.assign(new Error('timeout'), { name: 'AbortError' })), ms))]);
}

export async function signInWithPasskey(): Promise<void> {
  const optionsJSON = await api<Parameters<typeof startAuthentication>[0]['optionsJSON']>('POST', '/api/auth/passkey/login-options', {});
  const response = await withTimeout(startAuthentication({ optionsJSON }));
  await api('POST', '/api/auth/passkey/login', { response });
}

export async function addPasskey(): Promise<PasskeyDTO> {
  const optionsJSON = await api<Parameters<typeof startRegistration>[0]['optionsJSON']>('POST', '/api/me/passkeys/options', {});
  const response = await withTimeout(startRegistration({ optionsJSON }));
  return api<PasskeyDTO>('POST', '/api/me/passkeys', { response });
}

export const listPasskeys = () => api<PasskeyDTO[]>('GET', '/api/me/passkeys');
export const removePasskey = (id: string) => api('DELETE', `/api/me/passkeys/${encodeURIComponent(id)}`);
